import { useCallback, useMemo, useState } from 'react';

import { Tab } from '../../components/Tab';
import { USERS_ROUTE, USER_DETAIL_ROUTE_TAB } from '../../constants/routes.constants';

import { ResendEmailBanner } from './ResendEmailBanner.container';
import {
  UserProfileErrorBanner,
  UserProfileTabBannerErrorContext,
  TabBannerContextType,
} from './TabBanner.container';
import { USER_PROFILE_TABS } from './UserProfile.decl';

import { IDType } from '@aircall/tractor';
import { ThemeProvider } from '@aircall/tractor-v2';
import { PageHeader } from '@components/PageHeader';
import { NavigateToAccessibleRoute } from '@components/Permissions';
import { PendingValidationTag } from '@components/UserTag/UserTag';
import { PERMISSIONS_ACTIONS } from '@constants/permissions.constants';
import { Loading, getInitials } from '@dashboard/library';
import { GetUserQuery, GetUserQueryVariables } from '@generated/GetUserQuery';
import { GET_USER_QUERY } from '@graphql/queries/GetUserQuery';
import { NotFoundError } from '@helpers/errors.helpers';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { useHasAccess } from '@hooks/useHasAccess';
import { useNavigateWithParamsReplace } from '@hooks/useNavigateWithParamsReplace';
import { useGraphQuery } from '@hooks/useQuery';
import { UserProfileBusinessHoursPage } from '@pages/user-profile-business-hours/UserProfileBusinessHours.page';
import UserProfileCallPreferences from '@pages/user-profile-call-preferences/UserProfileCallPreferences.tab';
import { UserProfileGeneral } from '@pages/user-profile-general-info/UserProfileGeneral.tab';
import { UserProfileNotifications } from '@pages/user-profile-notifications/UserProfileNotifications.tab';
import { UserProfileNumbersTeamsTab } from '@pages/user-profile-numbers-and-teams/UserProfileNumbersTeams.tab';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Navigate } from 'react-router-dom';

const availableTabs: string[] = Object.values(USER_PROFILE_TABS);

export function UserProfileTabContainerInner() {
  const { t } = useTranslation();
  const { tab, userId } = useParams();
  const navigate = useNavigateWithParamsReplace();
  const [error, setError] = useState<TabBannerContextType['error']>(null);
  const [hasUsersReadAccess] = useHasAccess([PERMISSIONS_ACTIONS.READ]);
  const { data, loading } = useGraphQuery<GetUserQuery, GetUserQueryVariables>(GET_USER_QUERY, {
    variables: { agentID: userId },
    throwError: true,
  });

  const onTabChange = useCallback(
    (tabId: IDType) => {
      // clear the error on the banner before leaving the tab
      setError(null);

      navigate(USER_DETAIL_ROUTE_TAB, { userId, tab: tabId! });
    },
    [navigate, userId]
  );

  const memoizedErrorContextValue = useMemo(() => ({ error, setError }), [error, setError]);

  if (loading) {
    return (
      <ThemeProvider>
        <Loading data-test='user-profile-loading' />
      </ThemeProvider>
    );
  }

  if (tab && !availableTabs.includes(tab)) {
    throw new NotFoundError('Tab not found', { tab });
  }

  if (!tab && data?.getAgentV2) {
    return (
      <Navigate replace to={`${USERS_ROUTE}/${data?.getAgentV2.ID}/${USER_PROFILE_TABS.GENERAL}`} />
    );
  }

  const { getAgentV2: user } = data!;

  const { firstName, lastName, pictureURL, isActive, ID } = user;
  const initials = getInitials(firstName, lastName);

  return (
    <UserProfileTabBannerErrorContext.Provider value={memoizedErrorContextValue}>
      <PageHeader
        title={`${firstName} ${lastName}`}
        avatarSrc={pictureURL}
        avatarInitials={initials}
        activeTabId={tab}
        onTabChange={onTabChange}
        gobackLinkRoute={hasUsersReadAccess ? USERS_ROUTE : undefined}
        gobackLinkText={t('user_profile.header.back_to_users')}
        renderTitleRight={() =>
          !isActive && <PendingValidationTag data-test='user-profile-pending-validation-tag' />
        }
      >
        <Tab.Menu>
          <Tab.MenuItem id={USER_PROFILE_TABS.GENERAL}>
            {t('user_profile.general.title')}
          </Tab.MenuItem>
          <Tab.MenuItem id={USER_PROFILE_TABS.NUMBERS_TEAMS}>
            {t('user_profile.numbers_and_teams.title')}
          </Tab.MenuItem>
          <Tab.MenuItem id={USER_PROFILE_TABS.WORKING_HOURS}>
            {t('user_profile.working_hours.title')}
          </Tab.MenuItem>
          <Tab.MenuItem id={USER_PROFILE_TABS.CALL_PREFERENCES}>
            {t('user_profile.call_preferences.title')}
          </Tab.MenuItem>
          <Tab.MenuItem id={USER_PROFILE_TABS.NOTIFICATIONS}>
            {t('user_profile.notifications.title')}
          </Tab.MenuItem>
        </Tab.Menu>

        {!isActive ? <ResendEmailBanner userId={ID} /> : null}
        <UserProfileErrorBanner />

        <Tab.Content>
          <Tab.Item id={USER_PROFILE_TABS.GENERAL}>
            <UserProfileGeneral user={user} />
          </Tab.Item>
          <Tab.Item id={USER_PROFILE_TABS.NUMBERS_TEAMS}>
            <UserProfileNumbersTeamsTab user={user} />
          </Tab.Item>
          <Tab.Item id={USER_PROFILE_TABS.WORKING_HOURS}>
            <UserProfileBusinessHoursPage user={user} />
          </Tab.Item>
          <Tab.Item id={USER_PROFILE_TABS.CALL_PREFERENCES}>
            <UserProfileCallPreferences user={user} />
          </Tab.Item>
          <Tab.Item id={USER_PROFILE_TABS.NOTIFICATIONS}>
            <UserProfileNotifications user={user} />
          </Tab.Item>
        </Tab.Content>
      </PageHeader>
    </UserProfileTabBannerErrorContext.Provider>
  );
}

export function UserProfileTabContainer() {
  const { userId } = useParams();
  const { currentUser } = useGlobalData();
  const [hasUsersReadAccess] = useHasAccess([PERMISSIONS_ACTIONS.READ]);

  const isOwnUser = currentUser.ID === userId;
  const hasAccess = isOwnUser || hasUsersReadAccess;

  if (!hasAccess) {
    return <NavigateToAccessibleRoute />;
  }

  return <UserProfileTabContainerInner />;
}
